import find from "lodash/find";
import get from "lodash/get";
import toNumber from "lodash/toNumber";
import round from "lodash/round";
import mean from "lodash/mean";

const getAnswerObj = (quizzes, quizId, id) => {
  const quiz = find(quizzes, { id: quizId });
  return find(quiz.answers, { id });
};

const getBudgetSliderAnswers = (state, type = "budget") => {
  return state.answers.reduce((acc, curr) => {
    const budgetAnswer = curr.answer.find((a) => a.type === type);

    if (budgetAnswer) {
      // Check if value === max
      const answerObj = getAnswerObj(state.quizzes, curr.quiz, budgetAnswer.id);
      const maxValue = toNumber(answerObj[state.country]);
      budgetAnswer.isMax = maxValue <= budgetAnswer.value;

      // Add answer
      return [...acc, budgetAnswer];
    }

    return acc;
  }, []);
};

export const getBudgetWeighting = (products, state) => {
  const answers = getBudgetSliderAnswers(state);

  // No budget answer given, we'll skip it
  if (!answers.length) {
    return products.map((product) => ({ ...product, budgetWeighting: 1 }));
  }

  // Loop each product
  return products.map((product) => {
    // Loop budget answers, get mean weighting
    const weightings = answers.map((answer) => {
      let price = get(product, `prices.${state.country}.PRICE`);
      if (!price) price = get(product, `prices.${state.country}.VALUE`);

      // No pricing for this product, settle with the lowest %
      if (!price) return 0.85;

      // Budget answer isn't weighted...
      if (!answer.isWeighted) {
        // Budget is greater than product price, regardless of difference multiplier is 1.
        if (answer.value > price) return 1;

        // Use standard overpricing formular to work out 20% degradation
        const value = 1 - (price / answer.value - 1) / 0.2;
        return value < 0 ? 0 : value;
      }

      // Exact price...
      if (answer.value === price) return 1;

      // Underpriced product
      // 0.15 handles the difference from 85% - 100%
      if (answer.value > price) {
        return 0.85 + (price / answer.value) * 0.15;
      }

      // Overpriced product

      // Check if budget answer isMax (e.g. £800+)
      if (answer.isMax) return 1;

      // 0.2 is the 20% of overpricing allowed
      const value = 1 - (price / answer.value - 1) / 0.2;
      return value < 0 ? 0 : value;
    });

    // Get the mean of all budget weightings, and round to 4 decimal places
    const budgetWeighting = round(mean(weightings), 6);

    return { ...product, budgetWeighting };
  });
};

export const getContractWeighting = (products, state) => {
  const answers = getBudgetSliderAnswers(state, "contract");

  // No budget answer given, we'll skip it
  if (!answers.length) {
    return products.map((product) => ({ ...product, contractWeighting: 1 }));
  }

  // Loop each product
  return products.map((product) => {
    // Loop budget answers, get mean weighting
    const weightings = answers.map((answer) => {
      let price = get(product, `contract.${state.country}`);

      // No pricing for this product, settle with the lowest %
      if (!price) return 0.85;

      // Budget answer isn't weighted...
      if (!answer.isWeighted) {
        // Budget is greater than product price, regardless of difference multiplier is 1.
        if (answer.value > price) return 1;

        // Use standard overpricing formular to work out 20% degradation
        const value = 1 - (price / answer.value - 1) / 0.2;
        return value < 0 ? 0 : value;
      }

      // Exact price...
      if (answer.value === price) return 1;

      // Underpriced product
      // 0.15 handles the difference from 85% - 100%
      if (answer.value > price) {
        return 0.85 + (price / answer.value) * 0.15;
      }

      // Overpriced product

      // Check if budget answer isMax (e.g. £800+)
      if (answer.isMax) return 1;

      // 0.2 is the 20% of overpricing allowed
      const value = 1 - (price / answer.value - 1) / 0.2;
      return value < 0 ? 0 : value;
    });

    // Get the mean of all budget weightings, and round to 4 decimal places
    const contractWeighting = round(mean(weightings), 6);

    return { ...product, contractWeighting };
  });
};
